// packages/web-ui/src/state/eventLog.ts
//
// Migration final fix-pass — HIGH-3 (`<ActivityTab>` + `<DebugTab>` are static
// placeholders with no slice extension). Records SSE events fanned through
// the dispatcher (`sse/attach-to-chat-slice.ts`) so the UtilityDrawer's
// Activity tab can render a live log and the Debug tab can summarise the
// recent activity stream.
//
// Buffer is bounded at `MAX_EVENT_LOG_ENTRIES` (default 200) — newest entries
// at index 0; oldest fall off the tail. This matches the W2-S9 file-header
// comment ("buffer in slice, not component" — Pitfall #3) and bounds memory
// consumption irrespective of session length.
//
// Pure state slice. The dispatcher invokes `appendEventLog` defensively
// (typeof-guard around the action), so partial compositions used by tests
// silently no-op. SSE handling correctness is unaffected by the eventLog.

import { createStore } from 'zustand/vanilla';
import type { StateCreator } from 'zustand';

/** Cap on the in-memory event log buffer. */
export const MAX_EVENT_LOG_ENTRIES = 200;

/**
 * Source classification — used by the Activity tab's group-by chip strip
 * and by the DebugTab's summary counters. The string literal is also used
 * as the visible badge label in `<ActivityTab>`.
 */
export type EventLogSource =
  | 'chat'
  | 'kb'
  | 'validation'
  | 'files'
  | 'session'
  | 'system'
  | 'view';

/** A single recorded event. */
export interface EventLogEntry {
  /** Stable id — `<timestamp>-<monotonic-counter>`. Used as React key. */
  id: string;
  /** Wall-clock timestamp (`Date.now()`-style epoch ms). */
  timestamp: number;
  /** SSE event name (e.g. `chat:token`, `kb:changesetApplied`). */
  type: string;
  /** Source classification (see {@link EventLogSource}). */
  source: EventLogSource;
  /** Raw event payload — recorded verbatim for the expandable preview. */
  payload: unknown;
}

export interface EventLogSliceState {
  /** Entries newest-first. Length is capped at {@link MAX_EVENT_LOG_ENTRIES}. */
  entries: ReadonlyArray<EventLogEntry>;
}

export interface EventLogSliceActions {
  /**
   * Append a single entry to the log. Caller supplies `type` + `source` +
   * `payload`; the slice generates `id` + `timestamp`. The buffer is
   * trimmed to `MAX_EVENT_LOG_ENTRIES` after the push.
   */
  appendEventLog: (input: {
    type: string;
    source: EventLogSource;
    payload: unknown;
    /** Optional override (tests). Defaults to `Date.now()`. */
    timestamp?: number;
  }) => void;
  /** Clear all entries. */
  clearEventLog: () => void;
  /** Test/debug helper — resets to {@link EVENT_LOG_INITIAL_STATE}. */
  _resetEventLog: () => void;
}

export type EventLogSlice = EventLogSliceState & EventLogSliceActions;

export const EVENT_LOG_INITIAL_STATE: EventLogSliceState = {
  entries: [],
};

// Monotonic counter used to disambiguate two events that arrive within the
// same millisecond. Module-level (not slice state) so the id-collision
// guarantee survives `_resetEventLog` between tests.
let monotonicCounter = 0;

function nextEntryId(timestamp: number): string {
  monotonicCounter = (monotonicCounter + 1) % Number.MAX_SAFE_INTEGER;
  return `${timestamp}-${monotonicCounter}`;
}

/** Test-only — reset the monotonic counter between cases. */
export function _resetEventLogCounterForTests(): void {
  monotonicCounter = 0;
}

export const createEventLogSlice: StateCreator<
  EventLogSlice,
  [],
  [],
  EventLogSlice
> = (set) => ({
  ...EVENT_LOG_INITIAL_STATE,

  appendEventLog: (input) => {
    const ts = typeof input.timestamp === 'number' ? input.timestamp : Date.now();
    const entry: EventLogEntry = {
      id: nextEntryId(ts),
      timestamp: ts,
      type: input.type,
      source: input.source,
      payload: input.payload,
    };
    set((s) => {
      // Newest-first ordering. Cap at MAX_EVENT_LOG_ENTRIES.
      const next: EventLogEntry[] = [entry, ...s.entries];
      if (next.length > MAX_EVENT_LOG_ENTRIES) {
        next.length = MAX_EVENT_LOG_ENTRIES;
      }
      return { ...s, entries: next };
    });
  },

  clearEventLog: () => {
    set((s) => ({ ...s, entries: [] }));
  },

  _resetEventLog: () => {
    set({ ...EVENT_LOG_INITIAL_STATE } as EventLogSlice);
  },
});

/** Vanilla store factory for tests / non-shell call-sites. */
export function createEventLogStore() {
  return createStore<EventLogSlice>()(createEventLogSlice);
}

// ─── Selectors ───────────────────────────────────────────────────────────

export function selectEventLogEntries(s: EventLogSliceState): ReadonlyArray<EventLogEntry> {
  return s.entries;
}

/** Group entries by `type` preserving newest-first order within each group. */
export function selectEntriesGroupedByType(
  s: EventLogSliceState,
): ReadonlyArray<{ type: string; entries: ReadonlyArray<EventLogEntry> }> {
  const grouped = new Map<string, EventLogEntry[]>();
  for (const e of s.entries) {
    const bucket = grouped.get(e.type);
    if (bucket !== undefined) bucket.push(e);
    else grouped.set(e.type, [e]);
  }
  return [...grouped.entries()].map(([type, entries]) => ({ type, entries }));
}

/** Summary counters — used by `<DebugTab>`. */
export function selectEventLogSummary(s: EventLogSliceState): {
  total: number;
  bySource: Record<EventLogSource, number>;
  byType: Record<string, number>;
  oldestTimestamp: number | null;
  newestTimestamp: number | null;
} {
  const bySource: Record<EventLogSource, number> = {
    chat: 0,
    kb: 0,
    validation: 0,
    files: 0,
    session: 0,
    system: 0,
    view: 0,
  };
  const byType: Record<string, number> = {};
  for (const e of s.entries) {
    bySource[e.source] += 1;
    byType[e.type] = (byType[e.type] ?? 0) + 1;
  }
  return {
    total: s.entries.length,
    bySource,
    byType,
    oldestTimestamp: s.entries.length > 0 ? s.entries[s.entries.length - 1].timestamp : null,
    newestTimestamp: s.entries.length > 0 ? s.entries[0].timestamp : null,
  };
}
